import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { API_ERROR_CODES } from '@solar/contracts';

import { Alert } from '../../components/Alert';
import { authApi } from '../../features/auth/auth.api';
import { ApiError } from '../../lib/api-error';
import { toDisplayMessage, toFieldErrors } from '../../lib/error-message';
import { paths } from '../../routes/paths';

import styles from './AuthForm.module.css';
import { AuthFormField } from './AuthFormField';
import { AuthLayout } from './AuthLayout';

/** Matches default backend password policy (env.schema defaults). */
const PASSWORD_HINT =
  'At least 12 characters, including upper and lower case, a digit and a symbol.';

function TokenUnavailablePanel({ message }: { message: string }): React.JSX.Element {
  return (
    <AuthLayout
      title="Reset link unavailable"
      description={message}
      footer={
        <>
          <p>
            <Link to={paths.forgotPassword}>Request a new reset link</Link>
          </p>
          <p>
            <Link to={paths.login}>Back to sign in</Link>
          </p>
        </>
      }
    />
  );
}

export function ResetPasswordPage(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState(false);
  const [tokenUnavailable, setTokenUnavailable] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});

    if (password !== confirmation) {
      setFieldErrors({ confirmation: 'The two passwords do not match.' });
      return;
    }

    if (token === null || token === '') {
      setTokenUnavailable(true);
      return;
    }

    setBusy(true);

    try {
      await authApi.resetPassword(token, password);
      setDone(true);
    } catch (caught: unknown) {
      if (caught instanceof ApiError && caught.code === API_ERROR_CODES.INVALID_RESET_TOKEN) {
        setTokenUnavailable(true);
        return;
      }

      setError(toDisplayMessage(caught, 'Unable to reset your password. Please try again.'));
      setFieldErrors(toFieldErrors(caught));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthLayout
        title="Password updated"
        description="Your password has been reset successfully."
        footer={
          <p>
            <Link to={paths.login}>Sign in</Link>
          </p>
        }
      >
        <Alert tone="success">
          Your password has been reset successfully. Sign in with your new password.
        </Alert>
      </AuthLayout>
    );
  }

  if (tokenUnavailable || token === null || token === '') {
    const message =
      token === null || token === ''
        ? 'This reset link is incomplete. Open the link from your email exactly as it was sent.'
        : 'This password reset link is invalid or has expired.';

    return <TokenUnavailablePanel message={message} />;
  }

  return (
    <AuthLayout
      title="Choose a new password"
      description="Enter a new password for your account. You will need to sign in again afterwards."
      heroHeadline="Secure access to your Seamontech account."
      heroLead="Choose a strong password to protect your solar journey."
      footer={
        <p>
          <Link to={paths.login}>Back to sign in</Link>
        </p>
      }
    >
      <form className={styles.form} onSubmit={(event) => void handleSubmit(event)} noValidate>
        {error !== null && <Alert tone="error">{error}</Alert>}

        <AuthFormField
          label="New password"
          inputType="password"
          name="password"
          icon="lock"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
          autoComplete="new-password"
          placeholder="Create a strong password"
          hint={PASSWORD_HINT}
          error={fieldErrors.password}
          allowPasswordToggle
          required
        />

        <AuthFormField
          label="Confirm new password"
          inputType="password"
          name="confirmation"
          icon="lock"
          value={confirmation}
          onChange={(event) => {
            setConfirmation(event.target.value);
          }}
          autoComplete="new-password"
          placeholder="Re-enter your password"
          error={fieldErrors.confirmation}
          allowPasswordToggle
          required
        />

        <button className={styles.submitButton} type="submit" disabled={busy} aria-busy={busy}>
          {busy ? 'Resetting…' : 'Reset password'}
          {!busy && (
            <span className={styles.submitArrow} aria-hidden="true">
              →
            </span>
          )}
        </button>
      </form>
    </AuthLayout>
  );
}
